import { getNodeValue, ownerDocument, xmlToString } from '../../../xmlUtility';
import {
    paramDocId,
    paramDocIdXPath,
    paramDsName,
    paramKey,
    paramKeyXPath,
    paramTimeout,
    paramXPath,
} from '../constants/parameterNames';
import {
    typeDataSource,
    typeNumber,
    typeXPath,
} from '../constants/parameterTypes';
import { failure, success } from '../helpers/commandResult';
import { getDataConnection } from '../helpers/qRules.dataConnection.helpers';
import {
    getDataSource,
    verifySecondaryDataSource,
} from '../helpers/qRules.dataSource.helpers';
import { getValidNode, getValidNodeSet } from '../helpers/qRules.node.helpers';
import {
    DBXL_PI_NAME,
    setDbxlPiValues,
} from '../helpers/qRules.processingInstruction.helpers';
import submitDocumentWrapper from '../helpers/submitDocumentWrapper';

const $ = window.jQuery;

const optionalParameters = [
    {
        name: 'doctype',
        description:
            'Allows you to explicitly specify the doc type to submit to. This is necessary when the template is going to be accessed from somewhere other than DBXL (like SharePoint) or when you’re submitting a data source other than the main data source.',
    },
    {
        name: paramDocId,
        description: `DocID of the document you are submitting.\n\nIf /${paramDocId} is specified and /${paramXPath} is not, this docid will be stored in the data source in a ${DBXL_PI_NAME} processing instruction and will be included in subsequent submits unless it is deliberately modified or overridden in a subsequent SubmitToDbxl command.\n\nIgnored if /${paramXPath} is specified and matches multiple nodes.\n\nUse /${paramDocIdXPath} to specify DocIDs for multiple documents.`,
    },
    {
        name: paramKey,
        description: `Document key to use when submitting to the SubmitDocumentWithKey web method.\n\nWhen using that method, either this or /${paramKeyXPath} must be specified.\n\nIgnored if /${paramXPath} is specified and matches multiple nodes.\n\nUse /${paramKeyXPath} to specify keys for multiple documents.`,
    },
    {
        name: 'user',
        description:
            'If submitting via the SubmitDocumentAsUser web method, the user parameter must be provided.',
    },
    {
        name: 'name',
        description: 'Form name',
    },
    {
        name: 'author',
        description: 'Form author',
    },
    {
        name: 'desc',
        description: 'Form description',
    },
    {
        name: paramDsName,
        description:
            'Allows you to submit a datasource other than the main data source to DBXL. This can be used when you have child doc types and have added their XML as a 2ds',
        type: typeDataSource,
    },
    {
        name: paramTimeout,
        description:
            'Allows you to enter the timeout in secs. This can be used if you have large documents to submit to DBXL',
        type: typeNumber,
    },
    {
        name: paramXPath,
        description:
            'XPath to XML-tree node(s) to submit, if you are submitting only a portion of the xml',
        type: typeXPath,
        dsParentName: paramDsName,
    },
    {
        name: 'doctypexpath',
        description:
            'Relative XPath to submit doc type value; overrides /doctype param',
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: paramDocIdXPath,
        description: `XPath to locate the DocID for each document to submit. Relative to /${paramXPath} if /${paramXPath} is specified; otherwise, relative to the data source root.\n\nOverrides /${paramDocId}.`,
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: paramKeyXPath,
        description: `XPath to locate the Document Key for each document to submit. Relative to /${paramXPath} if /${paramXPath} is specified; otherwise, relative to the data source root.\n\nOverrides /${paramKey}.`,
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: 'namexpath',
        description: 'Relative XPath to Name value; overrides /name param',
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: 'authorxpath',
        description: 'Relative XPath to Author value; overrides /author param',
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: 'descxpath',
        description:
            'Relative XPath to Description value; overrides /desc param',
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: 'resultdocidxpath',
        description:
            'Relative XPath to store DocID value returned from successful submit',
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
    {
        name: 'resultrefidxpath',
        description:
            'Relative XPath to store RefID value returned from successful submit',
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramXPath,
    },
];

const requiredParameters = [
    {
        name: 'submit',
        description:
            "Name of the query (receive) data connection to DBXL's SubmitDocument, SubmitDocumentWithKey, or SubmitDocumentAsUser method.",
        type: typeDataSource,
    },
];

const submittodbxlErrorIncorrectSubmitAdapterSubmitdocumentWithKey =
        'The submit adapter must use the SubmitDocumentWithKey web method when using submit keys.',
    submittodbxlErrorIncorrectSubmitAdapterSubmitdocumentAsUser =
        'The submit adapter must use the SubmitDocumentAsUser web method when submitting as a specific user.',
    submittodbxlErrorIncorrectSubmitAdapterSubmitdocument =
        'The submit adapter must use the SubmitDocument web method when not using submit keys or submitting as another user.',
    submittodbxlErrorFailedToSelectNodeDoctype =
        'Failed to select the DocType node.',
    submittodbxlErrorFailedToSelectNodeDocid =
        'Failed to select the DocID node.',
    submittodbxlErrorFailedToSelectNodeKey = 'Failed to select the Key node.',
    submittodbxlErrorFailedToSelectNodeName = 'Failed to select the Name node.',
    submittodbxlErrorFailedToSelectNodeAuthor =
        'Failed to select the Author node.',
    submittodbxlErrorFailedToSelectNodeDesc =
        'Failed to select the Description node.',
    submitToDbxlErrorFailedToSelectNodeResultDocId =
        'Failed to select the Result DocID node.',
    submitToDbxlErrorFailedToSelectNodeResultRefId =
        'Failed to select the Result RefID node.',
    description = 'Submits the document to Qdabra DBXL.';

function verifyCorrectDbxlSubmitAdapter(
    submitSource,
    isKeySubmit,
    isUserSubmit,
) {
    var submitKey, erroMessage;

    if (isKeySubmit) {
        submitKey = 'SubmitDocumentWithKey';
        erroMessage =
            submittodbxlErrorIncorrectSubmitAdapterSubmitdocumentWithKey;
    } else if (isUserSubmit) {
        submitKey = 'SubmitDocumentAsUser';
        erroMessage =
            submittodbxlErrorIncorrectSubmitAdapterSubmitdocumentAsUser;
    } else {
        submitKey = 'SubmitDocument';
        erroMessage = submittodbxlErrorIncorrectSubmitAdapterSubmitdocument;
    }

    getValidNode(
        submitSource,
        String.format('//node()[local-name() = "{0}"]', submitKey),
        erroMessage,
    );
}

function getNodeValueIfSpecified(
    commonFunction,
    currentValue,
    currentNode,
    path,
    errorMessage,
) {
    return !path
        ? currentValue
        : commonFunction.getValidNode(currentNode, path, errorMessage).value();
}

function getValidDocId(docId) {
    if (!docId) {
        return null;
    }

    var intValue = parseInt(docId);

    if (Number.isNaN(intValue) || intValue === 0) {
        return null;
    }

    return docId;
}

function buildDomFromNode(commonFunctions, node, docId) {
    var doc = $.parseXML(xmlToString(node.getNode())),
        docElement = doc.documentElement,
        docParent = docElement.parentElement || docElement.ownerDocument,
        pis = commonFunctions
            .getDataSource()
            .selectNodes(
                "/processing-instruction()[local-name() != 'QdabraDBXL']",
            );

    pis.forEach(function (pi) {
        var piNode = pi.getNode(),
            piStr = getNodeValue(piNode),
            piNodeOwner = ownerDocument(piNode);

        //if (piNode.localName === 'mso-infoPathSolution')//TODO:Check localName or nodeName ??
        if (piNode.nodeName === 'mso-infoPathSolution') {
            var start = piStr.indexOf('solutionVersion'),
                end = piStr.indexOf('"', start + 20) + 1;

            piStr = piStr.substr(0, start) + piStr.substr(end);
        }

        //var piStrNode = $.parseXML(piStr);
        var piStrNode = piNodeOwner.createProcessingInstruction(
            'mso-infoPathSolution',
            piStr,
        );
        docParent.insertBefore(piStrNode, docElement);
    });

    // Add QdabraDBXL PI if DocID is present.
    if (docId) {
        var qdabraPi = doc.createProcessingInstruction(
            DBXL_PI_NAME,
            String.format('docid="{0}"', docId),
        );
        docParent.insertBefore(qdabraPi, docElement);
    }

    return doc;
}

const doSubmit = (
    submitDocumentWithKeyObj,
    submitDocumentAsUserObj,
    submitDocumentObj,
    isKeySubmit,
    isUserSubmit,
    docType,
    name,
    author,
    desc,
    key,
    userName,
    subdom,
) => {
    var xmlStr = xmlToString(subdom);

    if (isKeySubmit) {
        return submitDocumentWithKeyObj.querySubmitDocumentWithKey(
            docType,
            xmlStr,
            name,
            author,
            desc,
            key,
        );
    }

    if (isUserSubmit) {
        return submitDocumentAsUserObj.querySubmitDocumentAsUser(
            docType,
            userName,
            xmlStr,
            name,
            author,
            desc,
        );
    }

    return submitDocumentObj.querySubmitDocument(
        docType,
        xmlStr,
        name,
        author,
        desc,
    );
};

const setDocIdField = async (node, docId, resultdocIdxpath) => {
    if (!resultdocIdxpath) {
        return;
    }

    const docIdNode = getValidNode(
        node,
        resultdocIdxpath,
        submitToDbxlErrorFailedToSelectNodeResultDocId,
    );

    if (!docIdNode) {
        return;
    }

    await docIdNode.setValueAsync(docId);
};

const setRefIdField = async (node, refid, resultrefidxpath) => {
    if (!resultrefidxpath) {
        return null;
    }
    var refIdNode = getValidNode(
        node,
        resultrefidxpath,
        submitToDbxlErrorFailedToSelectNodeResultRefId,
    );

    if (!refIdNode) {
        return null;
    }

    return refIdNode.setValueAsync(refid);
};

function SubmitToDbxl(params) {
    const { commonFunctions, utilities } = params;

    function getXmlToSubmit(dom, node, xpath, docId, docType) {
        if (xpath) {
            return buildDomFromNode(commonFunctions, node, docId);
        }

        if (docId) {
            setDbxlPiValues(dom.getNode(), docId, docType, '', '', '');
        }

        return dom.getNode();
    }

    async function executeAsync({ dataConnections, dataSources }) {
        var submitConnectionName = commonFunctions.getParamValue('submit'),
            docType = commonFunctions.getParamValue('doctype', ''),
            dsname = commonFunctions.getParamValue(paramDsName),
            docId = commonFunctions.getParamValue(paramDocId),
            key = commonFunctions.getParamValue(paramKey),
            userName = commonFunctions.getParamValue('user'),
            name = commonFunctions.getParamValue('name', ''),
            author = commonFunctions.getParamValue('author', ''),
            desc = commonFunctions.getParamValue('desc', ''),
            xpath = commonFunctions.getParamValue(paramXPath),
            doctypexpath = commonFunctions.getParamValue('doctypexpath'),
            docIdxpath = commonFunctions.getParamValue(paramDocIdXPath),
            keyxpath = commonFunctions.getParamValue(paramKeyXPath),
            namexpath = commonFunctions.getParamValue('namexpath'),
            authorxpath = commonFunctions.getParamValue('authorxpath'),
            descxpath = commonFunctions.getParamValue('descxpath'),
            resultdocIdxpath =
                commonFunctions.getParamValue('resultdocidxpath'),
            resultrefidxpath =
                commonFunctions.getParamValue('resultrefidxpath'),
            timeout = commonFunctions.getIntParamValue(paramTimeout),
            refid,
            isKeySubmit = !!key || !!keyxpath,
            isUserSubmit = !!userName;

        verifySecondaryDataSource(dataSources, submitConnectionName);

        const submitConnection = getDataConnection(
            dataConnections,
            submitConnectionName,
        );
        const submitSource = getDataSource(dataSources, submitConnectionName);

        verifyCorrectDbxlSubmitAdapter(submitSource, isKeySubmit, isUserSubmit);

        var submitDocumentObj = submitDocumentWrapper.standard(
                submitSource,
                submitConnection,
            ),
            submitDocumentAsUserObj = utilities.submitDocumentAsUser(
                submitSource.getNode(),
                submitConnection,
            ),
            submitDocumentWithKeyObj = submitDocumentWrapper.withKey(
                submitSource,
                submitConnection,
            ),
            dom = commonFunctions.getDataSource(dsname);

        if (timeout > 0) {
            submitConnection.timeOut = timeout;
        }

        var nodes = xpath ? getValidNodeSet(dom, xpath) : [dom];

        for (const node of nodes) {
            // BUGBUG - The logic for determining docid and key is hard to follow because they are reassigned in multiple places.
            //          Should have a function like determineDocIdToUse() that returns the correct value.
            if (nodes.length > 1) {
                // These values must be unique if there is more than one row being submitted.
                docId = null;
                key = null;
            }

            docType = getNodeValueIfSpecified(
                commonFunctions,
                docType,
                node,
                doctypexpath,
                submittodbxlErrorFailedToSelectNodeDoctype,
            );
            docId = getNodeValueIfSpecified(
                commonFunctions,
                docId,
                node,
                docIdxpath,
                submittodbxlErrorFailedToSelectNodeDocid,
            );
            key = getNodeValueIfSpecified(
                commonFunctions,
                key,
                node,
                keyxpath,
                submittodbxlErrorFailedToSelectNodeKey,
            );
            name = getNodeValueIfSpecified(
                commonFunctions,
                name,
                node,
                namexpath,
                submittodbxlErrorFailedToSelectNodeName,
            );
            author = getNodeValueIfSpecified(
                commonFunctions,
                author,
                node,
                authorxpath,
                submittodbxlErrorFailedToSelectNodeAuthor,
            );
            desc = getNodeValueIfSpecified(
                commonFunctions,
                desc,
                node,
                descxpath,
                submittodbxlErrorFailedToSelectNodeDesc,
            );
            docId = getValidDocId(docId);

            const subdom = getXmlToSubmit(dom, node, xpath, docId, docType);

            const submitResult = await doSubmit(
                submitDocumentWithKeyObj,
                submitDocumentAsUserObj,
                submitDocumentObj,
                isKeySubmit,
                isUserSubmit,
                docType,
                name,
                author,
                desc,
                key,
                userName,
                subdom,
            );

            if (!submitResult.success) {
                throw new Error(
                    submitResult.error || 'An unspecified error occurred',
                );
            }

            setDbxlPiValues(
                subdom,
                parseInt(submitResult.docId),
                docType,
                name,
                author,
                desc,
            );

            docId = submitResult.docId;
            refid = submitResult.refId;

            await setDocIdField(node, docId, resultdocIdxpath);

            await setRefIdField(node, refid, resultrefidxpath);
        }

        const resultValue = xpath ? '' : docId;

        return docId
            ? success(resultValue)
            : failure('Did not submit any documents');
    }

    return {
        description,
        executeAsync,
        optionalParameters,
        requiredParameters,
    };
}

export default SubmitToDbxl;
